// US-BUCKET-09 -- child submits a move-money request between two of
// their own buckets. Balance does NOT change here; it moves only
// when the guardian approves (US-BUCKET-10).

import {
  type AppError,
  type BucketEntry,
  type TransferSubmitResult,
  transferSubmit,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { AmountPad } from '../components/AmountPad';

type Props = {
  sessionId: string;
  /** All active buckets for this child -- both source picker and
   *  target picker draw from this list. */
  buckets: BucketEntry[];
  currencyCode: string;
  onSubmitted: (result: TransferSubmitResult) => void;
  onCancel: () => void;
};

const humanReadableError = (err: AppError): string => {
  if (err.kind === 'validation') return err.message;
  if (err.kind === 'not_found') return err.message;
  if (err.kind === 'unauthorized') return err.message;
  if (err.kind === 'network_required') return err.message;
  // Surface internal errors too so problems are visible rather than silent.
  return err.message ?? 'Something went wrong. Please try again.';
};

export const MoveMoneyScreen: Component<Props> = (props) => {
  // Default source: first active bucket with positive balance, or
  // first active bucket overall. Default target: any other active
  // bucket. Both fall back to undefined when there aren't enough
  // buckets to make a transfer (rare in practice but the form
  // should still render with a friendly message).
  const initialSource = () =>
    props.buckets.find((b) => b.balanceMinorUnits > 0)?.bucketId ??
    props.buckets[0]?.bucketId ??
    null;
  const [sourceBucketId, setSourceBucketId] = createSignal<number | null>(initialSource());
  const [targetBucketId, setTargetBucketId] = createSignal<number | null>(
    props.buckets.find((b) => b.bucketId !== initialSource())?.bucketId ?? null,
  );
  const [amountInput, setAmountInput] = createSignal('');
  const [note, setNote] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const sourceBucket = createMemo(() => props.buckets.find((b) => b.bucketId === sourceBucketId()));

  const parseMinorUnits = (): number | null => {
    const raw = amountInput().trim();
    if (raw === '') return null;
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const major = Number.parseFloat(raw);
    if (!Number.isFinite(major) || major <= 0) return null;
    return Math.round(major * 100);
  };

  const formatBalance = (minor: number) => {
    const major = minor / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: props.currencyCode,
      }).format(major);
    } catch {
      return `${props.currencyCode} ${major.toFixed(2)}`;
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    const src = sourceBucketId();
    const tgt = targetBucketId();
    if (src === null || tgt === null) {
      setErrorMessage('Pick a source and a target bucket.');
      return;
    }
    if (src === tgt) {
      setErrorMessage('Source and target buckets must be different.');
      return;
    }
    const minorUnits = parseMinorUnits();
    if (minorUnits === null) {
      setErrorMessage('Enter an amount greater than zero (e.g. 1 or 2.50).');
      return;
    }
    const source = sourceBucket();
    if (source && minorUnits > source.balanceMinorUnits) {
      setErrorMessage(
        `Not enough in ${source.bucketName} -- you have ${formatBalance(source.balanceMinorUnits)}.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const trimmedNote = note().trim();
      const result = await transferSubmit({
        sessionId: props.sessionId,
        sourceBucketId: src,
        targetBucketId: tgt,
        amountMinorUnits: minorUnits,
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      props.onSubmitted(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      class="action-screen--child-money"
      eyebrow="Mum & Dad approve first"
      title="Move money"
      onBack={props.onCancel}
      footer={
        <Show when={props.buckets.length >= 2}>
          <button
            type="button"
            class="primary action-screen-primary"
            disabled={submitting()}
            data-testid="move-money-submit"
            onClick={() => void handleSubmit()}
          >
            {submitting() ? 'Submitting…' : 'Send for approval'}
          </button>
        </Show>
      }
    >
      <p class="action-screen-intro">
        A guardian needs to approve before the money moves. You'll see this in your "My requests"
        section while it's waiting.
      </p>

      <Show when={props.buckets.length < 2}>
        <p class="error-message">
          You need at least two active buckets to move money. Add another bucket first.
        </p>
      </Show>

      <Show when={props.buckets.length >= 2}>
        <div class="action-form">
          <label class="field">
            <span class="field-label">From</span>
            <select
              value={sourceBucketId() ?? ''}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setSourceBucketId(v === '' ? null : Number.parseInt(v, 10));
                setErrorMessage(null);
              }}
              disabled={submitting()}
              data-testid="move-money-source"
            >
              <For each={props.buckets}>
                {(b) => (
                  <option value={b.bucketId}>
                    {b.bucketName} -- {formatBalance(b.balanceMinorUnits)}
                  </option>
                )}
              </For>
            </select>
          </label>

          <label class="field">
            <span class="field-label">To</span>
            <select
              value={targetBucketId() ?? ''}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setTargetBucketId(v === '' ? null : Number.parseInt(v, 10));
                setErrorMessage(null);
              }}
              disabled={submitting()}
              data-testid="move-money-target"
            >
              <For each={props.buckets.filter((b) => b.bucketId !== sourceBucketId())}>
                {(b) => <option value={b.bucketId}>{b.bucketName}</option>}
              </For>
            </select>
          </label>

          <AmountPad
            id="move-money-amount"
            label="Amount"
            value={amountInput()}
            currencyCode={props.currencyCode}
            hint={
              sourceBucket()
                ? `Available in ${sourceBucket()?.bucketName}: ${formatBalance(sourceBucket()?.balanceMinorUnits ?? 0)}`
                : undefined
            }
            disabled={submitting()}
            onChange={(value) => {
              setAmountInput(value);
              setErrorMessage(null);
            }}
          />

          <label class="field">
            <span class="field-label">Note (optional)</span>
            <input
              type="text"
              value={note()}
              onInput={(e) => setNote(e.currentTarget.value)}
              disabled={submitting()}
              placeholder="Why are you moving this money?"
              maxlength="500"
            />
          </label>

          <Show when={errorMessage()}>{(msg) => <p class="error-message">{msg()}</p>}</Show>
        </div>
      </Show>
    </ActionScreen>
  );
};
